import Search from 'antd/es/input/Search';
import { SortOrder } from 'antd/es/table/interface';
import { ParamsType } from '@ant-design/pro-provider';
import { PlusOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ActionType, ProColumns, RequestData } from '@ant-design/pro-table';
import { Badge, Button, FormInstance, message } from 'antd';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import Table from '../../../Common/Table';
import { useAuth } from '../../../../context/auth';
import CustomersStatuses from '../../../../enums/customers';
import { getMessageInError } from '../../../../hooks/fetch';
import { useContextCustomers } from '../../../../context/customers';
import { ModalState, ModalTypes } from '../../../../types';
import { Customer, useCustomersGet } from '../../../../hooks/customers';
import { getSorterParams, queryFilterParams } from '../../../../utils';

interface TableCustomers {
  openModal: (modal: ModalState) => void;
}

const TableCustomers: React.FC<TableCustomers> = ({ openModal }): JSX.Element => {
  const { isRoleEnough } = useAuth();
  const formRef = useRef<FormInstance>();
  const navigate = useNavigate();
  const customersGet = useCustomersGet();
  const actionRef = useRef<ActionType>();
  const { customerCreate, customerUpdate } = useContextCustomers();
  const [searchParams, setSearchParams] = useSearchParams();

  const [searchValue, setSearchValue] = useState<string>(searchParams.get('search') || '');

  const onSearch = (value: string) => {
    setSearchParams(queryFilterParams({
      current: searchParams.get('current') || '',
      pageSize: searchParams.get('pageSize') || '',
      search: value,
      name: searchParams.get('name') || '',
      email: searchParams.get('email') || '',
      status: searchParams.get('status') || '',
      orderByColumn: searchParams.get('orderByColumn') || '',
      orderBy: searchParams.get('orderBy') || '',
    }));
    formRef.current?.submit();
  };

  useEffect(() => {
    if ((customerCreate?.data && !customerCreate?.error) || (customerUpdate?.data && !customerUpdate.error)) {
      actionRef.current?.reload();
    }
  }, [customerCreate?.data, customerUpdate?.data]);

  useEffect(() => {
    if (customersGet.error) {
      message.error(getMessageInError(customersGet.error));
      customersGet.clearError();
    }
  }, [customersGet.error]);

  useEffect(() => {
    setSearchValue(searchParams.get('search') || '');
  }, [searchParams.get('search')]);

  const toolBarRender = useCallback(() => [
    <Search
      key="search"
      value={searchValue}
      style={{ width: 264 }}
      onSearch={onSearch}
      onChange={(value) => setSearchValue(value.target.value)}
      placeholder="Search"
    />,
    <>
      {isRoleEnough('manager') && (
        <Button
          key="button"
          icon={<PlusOutlined />}
          type="primary"
          onClick={() => openModal({ type: ModalTypes.create })}
        >
          Add New
        </Button>
      )}
    </>,
  ], [searchValue, onSearch]);

  const tableRequest = (
    { current, pageSize, ...args }: Record<string, string>
      & { pageSize?: number | undefined; current?: number | undefined; keyword?: string | undefined; },
    sorter: Record<string, SortOrder>,
  ): Promise<Partial<RequestData<Customer>>> => {
    const params = queryFilterParams({
      page: current ? `${current}` : '1',
      limit: pageSize ? `${pageSize}` : '10',
      ...args,
      ...getSorterParams(sorter),
    });

    setSearchParams({ ...args, ...getSorterParams(sorter) });

    return customersGet.fetch(params).then((data) => {
      if (data) {
        return ({ data: data.items || [], success: true, total: data.meta.totalItems });
      }

      return ({ data: [], success: false, total: 0 });
    });
  };

  const beforeSearchSubmit = useCallback((beforeSubmitParams: Partial<ParamsType>) => {
    const params = queryFilterParams({
      ...beforeSubmitParams,
      _timestamp: '',
      search: searchParams.get('search') || '',
    });

    setSearchParams(params);

    return params;
  }, [searchParams.get('search')]);

  const columns: ProColumns<Customer>[] = [
    {
      title: <span key="name">Name</span>,
      dataIndex: 'name',
      sorter: true,
      renderText: (name, { id }) => (
        <>
          {isRoleEnough('manager') ? (
            <Button type="link" onClick={() => openModal({ type: ModalTypes.update, id })}>
              {name}
            </Button>
          ) : name}
        </>
      ),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      sorter: true,
    },
    {
      title: 'Qty. templates',
      dataIndex: 'templateCount',
      hideInSearch: true,
      sorter: true,
      renderText: (templateCount, { id }) => (
        <Button
          key="button"
          type="link"
          onClick={() => navigate(`/templates?customer=${id}`, { replace: true })}
        >
          {templateCount}
        </Button>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      sorter: true,
      valueEnum: CustomersStatuses,
      renderText: (status) => <Badge status={status ? 'success' : 'default'} text={status ? 'Active' : 'Archived'} />,
    },
  ];

  return (
    <>
      <Table<Customer>
        formRef={formRef}
        columns={columns}
        request={tableRequest}
        actionRef={actionRef}
        headerTitle="Customers"
        columnsState={{ persistenceKey: 'pro-table-commodities', persistenceType: 'localStorage' }}
        toolBarRender={toolBarRender}
        showSorterTooltip={false}
        beforeSearchSubmit={beforeSearchSubmit}
        scroll={{ x: 1000, y: 500 }}
      />
    </>
  );
};

export default TableCustomers;
